import { Alert, Button, Empty, Spin, Tag, Typography } from "antd"
import React, { FC, useContext, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory, useLocation, useParams } from "react-router-dom"
import QueryErrorAlert from "../../components/QueryErrorAlert"
import formatDate from "../../helpers/formatDate"
import { useCurrency } from "../../helpers/currencyHelper"
import { CurrencyShort } from "../../helpers/renderAmount"
import { renderAsMoney } from "../../helpers/renderAsMoney"
import { getStoredBookerId } from "../../helpers/getStoredBookerId"
import { AccountBookerBookingsQuery, useAccountBookerBookingsQuery } from "../../generated/graphql"
import { SettingsContext } from "../../layouts/Main/MainLayout"
import { URL_ACCOUNT_BOOKINGS, URL_COURSE_DETAILS, URL_PAYMENT_RETURN, URL_WIDERRUF } from "../../models/url"
import { SubHeader } from "./AccountDashboard"
import { STATUS_TAG, WithAttendeeName, findNextOpenOnlinePayment, flattenBookerBookings, getBookingStatus, isPastBooking, safeDate } from "./bookingStatus"
import Tarif595BookingReceipts from "./Tarif595BookingReceipts"
import css from "./AccountBookingDetails.module.css"

const { Text } = Typography

type BookerAttendee = NonNullable<NonNullable<AccountBookerBookingsQuery["booker"]>["attendees"]>[number]
type Booking = WithAttendeeName<NonNullable<NonNullable<BookerAttendee>["bookings"]>[number]>
type Lesson = Booking["courseLessons"][number]

/** Deutsche Anzeige-Labels je PaymentType (kein bestehendes Mapping im Repo — TableColumnRenderers rendert nur tote i18n-Platzhalter). */
const PAYMENT_TYPE_LABEL: Record<string, { key: string; fallback: string }> = {
    PrePaid: { key: "account.paymentTypeLabel.prePaid", fallback: "Vorkasse" },
    Monthly: { key: "account.paymentTypeLabel.monthly", fallback: "Monatlich" },
    OnTheSpot: { key: "account.paymentTypeLabel.onTheSpot", fallback: "Vor Ort" },
    Bill: { key: "account.paymentTypeLabel.bill", fallback: "Rechnung" },
    Custom: { key: "account.paymentTypeLabel.custom", fallback: "Individuell" },
}

/** "Mo. 12.05.2026 · 10:00–11:00 Uhr" — defensiv gegen fehlende Zeiten. */
const lessonWhen = (lesson: Lesson): string => {
    const start = safeDate(lesson.startDateTime)
    const end = safeDate(lesson.endDateTime)
    if (!start) return "—"
    const day = formatDate(start, "EE dd.MM.yyyy")
    const time = `${formatDate(start, "HH:mm")}${end ? `–${formatDate(end, "HH:mm")}` : ""}`
    return `${day} · ${time} Uhr`
}

const AccountBookingDetails: FC = () => {
    const { t } = useTranslation()
    const currency = useCurrency().short as CurrencyShort
    const location = useLocation()
    const history = useHistory()
    const { bookingId } = useParams<{ bookingId: string }>()
    const withdrawalEnabled = useContext(SettingsContext).withdrawalButtonEnabled === true
    const tarif595Enabled = useContext(SettingsContext).tarif595Enabled === true
    const bookerId = getStoredBookerId()

    // Gleiche Query wie die Buchungsliste → beim Absprung von dort ein Cache-Hit.
    const { data, loading, error, refetch } = useAccountBookerBookingsQuery({
        skip: !bookerId,
        variables: { bookerId: bookerId ?? "" },
        fetchPolicy: "cache-and-network",
    })

    const booking: Booking | undefined = useMemo(
        () => flattenBookerBookings(data?.booker?.attendees).find((candidate) => candidate.id === bookingId),
        [data, bookingId],
    )

    const title = t("account.bookingDetails.title", "Buchungsdetails")

    if (loading && !booking) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <div className={css.loading} data-testid='account-booking-details-loading'>
                    <Spin spinning />
                </div>
            </div>
        )
    }

    if (error && !booking) {
        return (
            <div className={css.page}>
                <SubHeader title={title} />
                <QueryErrorAlert testId='account-booking-details-error' message={t("account.loadError", "Dein Benutzerkonto konnte nicht geladen werden.")} onRetry={() => refetch()} />
            </div>
        )
    }

    if (!booking) {
        return (
            <div className={css.page} data-testid='account-booking-details-notfound'>
                <SubHeader title={title} />
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("account.bookingDetails.notFound", "Diese Buchung wurde nicht gefunden.")} />
                <div className={css.notFoundBack}>
                    <Link to={URL_ACCOUNT_BOOKINGS}>{t("account.backToBookings", "Zurück zu meinen Buchungen")}</Link>
                </div>
            </div>
        )
    }

    const now = new Date()
    const status = getBookingStatus(booking, now)
    const tag = STATUS_TAG[status]
    const past = isPastBooking(booking, now)
    const course = booking.course
    const bookingReference = `${booking.bookingNumberPrefix ?? ""}${booking.bookingNumber ?? ""}`
    const createDate = safeDate(booking.createDate)
    const payDate = safeDate(booking.payDate)
    const courseLabel = [course?.courseType?.name, course?.prefixedCourseNumber].filter(Boolean).join(" · ") || t("account.bookings.unknownCourse", "Kurs")
    const paymentLabel = booking.paymentType ? PAYMENT_TYPE_LABEL[booking.paymentType] : undefined

    const lessons = [...booking.courseLessons].sort(
        (a, b) => (safeDate(a.startDateTime)?.getTime() ?? Infinity) - (safeDate(b.startDateTime)?.getTime() ?? Infinity),
    )
    const scheduledPayments = booking.scheduledPayments ?? []
    // Offene Online-Rate (Mollie) → "Jetzt bezahlen" führt auf die bestehende
    // Return-Seite. bookingId als Query-Param: für Raten ohne bisherigen
    // Zahlungsversuch kann die Return-Seite sonst keine initiate-Mutation starten
    // (kein localStorage-Kontext, Status-Query liefert null).
    const pendingOnlinePayment = findNextOpenOnlinePayment(booking)
    const goToPayment = () => {
        if (!pendingOnlinePayment) return
        history.push({
            pathname: URL_PAYMENT_RETURN.replace(":spId", pendingOnlinePayment.id),
            search: `?bookingId=${encodeURIComponent(booking.id)}`,
        })
    }

    return (
        <div className={css.page} data-testid='account-booking-details'>
            <SubHeader title={title} />

            {pendingOnlinePayment && (
                <Alert
                    type='warning'
                    showIcon
                    className={css.paymentAlert}
                    data-testid='account-booking-details-payment-alert'
                    message={t("account.payment.notPaidTitle", "Diese Buchung ist noch nicht bezahlt.")}
                    action={
                        <Button type='primary' onClick={goToPayment} data-testid='account-booking-details-pay-now'>
                            {t("account.payment.payNow", "Jetzt bezahlen")}
                        </Button>
                    }
                />
            )}

            <section className={css.section} data-testid='account-booking-details-master'>
                <div className={css.sectionTitle}>{t("account.bookingDetails.master", "Buchung")}</div>
                <dl className={css.list}>
                    <dt>{t("account.bookingDetails.number", "Buchungsnummer")}</dt>
                    <dd>{bookingReference || "—"}</dd>
                    <dt>{t("account.bookingDetails.createDate", "Gebucht am")}</dt>
                    <dd>{createDate ? formatDate(createDate, "dd.MM.yyyy") : "—"}</dd>
                    <dt>{t("account.bookingDetails.status", "Status")}</dt>
                    <dd>
                        <Tag color={tag.color}>{t(tag.key, tag.fallback)}</Tag>
                    </dd>
                    {booking.attendeeName && (
                        <>
                            <dt>{t("account.bookingDetails.attendee", "Teilnehmer")}</dt>
                            <dd>{booking.attendeeName}</dd>
                        </>
                    )}
                    <dt>{t("account.bookingDetails.course", "Kurs")}</dt>
                    <dd>
                        {course?.id ? (
                            <Link
                                // fromUrl: Der "Zurück zu meinen Buchungen"-Balken der Kursdetails springt hierher zurück.
                                // fromActiveBooking: Kursdetails benennen den Buchen-Button um ("Weiteren Teilnehmer buchen")
                                to={{
                                    pathname: URL_COURSE_DETAILS.replace(":id", course.id),
                                    state: { fromUrl: { pathname: location.pathname, search: location.search || "" }, ...(!past && { fromActiveBooking: true }) },
                                }}
                                data-testid='account-booking-details-course-link'
                            >
                                {courseLabel}
                            </Link>
                        ) : (
                            courseLabel
                        )}
                    </dd>
                </dl>
            </section>

            <section className={css.section} data-testid='account-booking-details-lessons'>
                <div className={css.sectionTitle}>{t("account.bookingDetails.lessons", "Termine")}</div>
                {lessons.length === 0 ? (
                    <Text type='secondary'>{t("account.bookingDetails.noLessons", "Keine Termine vorhanden")}</Text>
                ) : (
                    <ul className={css.lessonList}>
                        {lessons.map((lesson) => (
                            <li key={lesson.id} className={css.lessonRow}>
                                <span>{lessonWhen(lesson)}</span>
                                {(lesson.location?.name || lesson.location?.city) && (
                                    <Text type='secondary'>{[lesson.location?.name, lesson.location?.city].filter(Boolean).join(", ")}</Text>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className={css.section} data-testid='account-booking-details-payment'>
                <div className={css.sectionTitle}>{t("account.bookingDetails.payment", "Preis & Zahlung")}</div>
                <dl className={css.list}>
                    <dt>{t("account.bookingDetails.price", "Preis")}</dt>
                    <dd>{renderAsMoney(booking.grossPrice ?? undefined, "", currency) ?? "—"}</dd>
                    {(booking.discount ?? 0) > 0 && (
                        <>
                            {/* Rabattcode steht als Bezeichnung an der Buchung („Rabattcode BISCHBERG15“), Skizze 09. */}
                            <dt data-testid='account-booking-details-discount'>{booking.discountName || t("account.bookingDetails.discount", "Rabatt")}</dt>
                            <dd>{`- ${renderAsMoney(booking.discount ?? undefined, "", currency) ?? ""} (${t("discountCode.alreadyDeducted", "bereits abgezogen")})`}</dd>
                        </>
                    )}
                    <dt>{t("account.bookingDetails.paymentType", "Zahlweise")}</dt>
                    <dd>{paymentLabel ? t(paymentLabel.key, paymentLabel.fallback) : "—"}</dd>
                    {payDate && (
                        <>
                            <dt>{t("account.bookingDetails.payDate", "Bezahlt am")}</dt>
                            <dd>{formatDate(payDate, "dd.MM.yyyy")}</dd>
                        </>
                    )}
                </dl>
                {scheduledPayments.length > 0 && (
                    <ul className={css.paymentList} data-testid='account-booking-details-scheduled-payments'>
                        {scheduledPayments.map((payment) => {
                            const due = safeDate(payment.paymentDue)
                            // Vollständig beglichene Raten (openAmount <= 0, z.B. Gutschrift ohne
                            // gesetztes paymentDone-Datum) zählen als bezahlt — sonst stünde dort
                            // "0,00 · Offen". Teilzahlung: RESTBETRAG zeigen (openAmount), nicht
                            // die ursprüngliche Ratenhöhe — sonst liest der Kunde bei 60-€-Rate
                            // mit 20 € Rest fälschlich "60 € Offen".
                            const isPaid = !!payment.paymentDone || (payment.openAmount != null && payment.openAmount <= 0)
                            const displayAmount = isPaid ? payment.paymentAmount : payment.openAmount ?? payment.paymentAmount
                            return (
                                <li key={payment.id} className={css.paymentRow}>
                                    <span>
                                        {t("account.bookingDetails.paymentDue", {
                                            date: due ? formatDate(due, "dd.MM.yyyy") : "—",
                                            defaultValue: "fällig am {{date}}",
                                        })}
                                    </span>
                                    <span className={css.paymentAmount}>{renderAsMoney(displayAmount, "", currency) ?? "—"}</span>
                                    <Tag color={isPaid ? "green" : "gold"}>
                                        {isPaid ? t("account.bookingDetails.paid", "Bezahlt") : t("account.bookingDetails.open", "Offen")}
                                    </Tag>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </section>

            {tarif595Enabled && !booking.canceled && booking.bookingType !== "Waitlist" && <Tarif595BookingReceipts bookingId={booking.id} />}

            {!past && status === "booked" && withdrawalEnabled && (
                <section className={css.section} data-testid='account-booking-details-actions'>
                    <div className={css.sectionTitle}>{t("account.bookingDetails.actions", "Aktionen")}</div>
                    <Link to={{ pathname: URL_WIDERRUF, search: `?bookingNumber=${encodeURIComponent(bookingReference)}` }} data-testid='account-booking-details-withdraw'>
                        {t("account.bookings.withdraw", "Widerruf")}
                    </Link>
                </section>
            )}
        </div>
    )
}

export default AccountBookingDetails
